import React, {
  useCallback,
  useEffect,
  createContext,
  useMemo,
  useContext,
} from 'react';

import { useSelector } from 'react-redux';

import { BuildStatus } from 'src/components/types';
import { getPullRequestRefetchBuildsDummy } from 'src/redux/pull-request/selectors';
import {
  useAuthRequestLazy,
  useAuthRequest,
} from 'src/services/auth-request-service/auth-request-service';

type BuildStatusOptions = {
  repositoryMetadataURL?: string;
  buildStatusURL?: string;
  children?: React.ReactNode;
};

export type BuildStatusResult = {
  isLoading: boolean;
  hasError: boolean;
  refetch: () => void;
  hasStatuses: boolean;
  builds: BuildStatus[];
  repositoryFullSlug?: string;
};

export type BuildStatusResultProp = {
  buildStatusResult: BuildStatusResult;
};

type MetadataResponse = {
  has_statuses: boolean;
};

type StatusResponse = {
  values: BuildStatus[];
};

export const useBuildStatusFetcher = ({
  repositoryMetadataURL,
  buildStatusURL,
}: BuildStatusOptions): BuildStatusResult => {
  const {
    data: metadata,
    isLoading: metadataIsLoading,
    hasError: metadataHasError,
    refetch: metadataRefetch,
  } = useAuthRequest<MetadataResponse>({
    url: repositoryMetadataURL,
  });

  const {
    data: status,
    isLoading: statusIsLoading,
    hasError: statusHasError,
    fetchData: statusFetch,
    clearData: statusClear,
  } = useAuthRequestLazy<StatusResponse>({ url: buildStatusURL });

  const hasStatuses = metadata?.has_statuses || false;
  const builds = status?.values || [];
  const hasError = statusHasError || metadataHasError;
  const isLoading = statusIsLoading || metadataIsLoading;

  // The page reload logic toggles this boolean whenever the builds data
  // needs to be refreshed. By including this value as a dependency in
  // the following data refetching useEffect hook, we achieve the
  // wanted behavior.
  const refetchBuildsDummy = useSelector(getPullRequestRefetchBuildsDummy);

  useEffect(() => {
    // This is an expensive request, so we need to make sure that
    // there are statuses to fetch before making the request
    if (hasStatuses) {
      statusFetch();
    } else {
      statusClear();
    }
  }, [statusFetch, statusClear, hasStatuses, refetchBuildsDummy]);

  const refetch = useCallback(() => {
    metadataRefetch();
    statusClear();
  }, [metadataRefetch, statusClear]);

  return { builds, hasStatuses, isLoading, hasError, refetch };
};

const BuildStatusContext = createContext<BuildStatusResult | null>(null);

export const BuildStatusProvider = ({
  repositoryMetadataURL,
  buildStatusURL,
  repositoryFullSlug,
  children,
}: BuildStatusOptions & {
  repositoryFullSlug?: string;
}) => {
  const { builds, hasStatuses, isLoading, hasError, refetch } =
    useBuildStatusFetcher({
      repositoryMetadataURL,
      buildStatusURL,
    });

  const value = useMemo(
    () => ({
      builds,
      hasStatuses,
      isLoading,
      hasError,
      refetch,
      repositoryFullSlug,
    }),
    [builds, hasStatuses, isLoading, hasError, refetch, repositoryFullSlug]
  );

  return (
    <BuildStatusContext.Provider value={value}>
      {children}
    </BuildStatusContext.Provider>
  );
};

export const useBuildStatus = () => {
  const value = useContext(BuildStatusContext);
  if (value === null) {
    throw new Error('useBuildStatus requires a parent BuildStatusProvider');
  }

  return value;
};

export const withBuildStatus =
  <T extends {}>(
    Component: React.ComponentType<
      React.PropsWithChildren<T & BuildStatusResultProp>
    >
  ) =>
  (props: T) => {
    const buildStatusResult = useBuildStatus();

    return <Component buildStatusResult={buildStatusResult} {...props} />;
  };
